import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { FiBookOpen, FiCopy, FiEdit2, FiImage, FiTrash2 } from 'react-icons/fi';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { ImageField } from '../../components/ImageField';
import { Input } from '../../components/Input';
import { QrCode } from '../../components/QrCode';
import { atualizarCategoria, criarCategoria, listarCategorias, removerCategoria } from '../../api/categorias';
import { useAuth } from '../../context/AuthContext';
import { Categoria } from '../../types';
import { lerArquivoComoDataUrl } from '../../utils/arquivos';

interface FormCategoria {
  nome: string;
  ordem: number | string;
  imagem_base64: string | null;
  remover_imagem: boolean;
}

const FORM_INICIAL: FormCategoria = { nome: '', ordem: 0, imagem_base64: null, remover_imagem: false };

export function CardapioGestao() {
  const { usuario } = useAuth();
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState<FormCategoria>(FORM_INICIAL);
  const [editando, setEditando] = useState<Categoria | null>(null);
  const [salvando, setSalvando] = useState(false);

  const linkPublico = `${window.location.origin}/cardapio/${usuario?.tenant_id}`;

  function carregar() {
    setCarregando(true);
    listarCategorias()
      .then(setCategorias)
      .finally(() => setCarregando(false));
  }

  useEffect(carregar, []);

  const categoriasOrdenadas = useMemo(
    () => [...categorias].sort((a, b) => (a.ordem === b.ordem ? a.nome.localeCompare(b.nome) : a.ordem - b.ordem)),
    [categorias]
  );

  function handleChange(campo: keyof FormCategoria) {
    return (event: ChangeEvent<HTMLInputElement>) =>
      setForm((atual) => ({ ...atual, [campo]: event.target.value }));
  }

  function iniciarEdicao(categoria: Categoria) {
    setEditando(categoria);
    setForm({
      nome: categoria.nome,
      ordem: categoria.ordem,
      imagem_base64: null,
      remover_imagem: false,
    });
  }

  function cancelarEdicao() {
    setEditando(null);
    setForm(FORM_INICIAL);
  }

  async function handleImagem(file: File | null) {
    if (!file) return;
    try {
      const imagem = await lerArquivoComoDataUrl(file);
      setForm((atual) => ({ ...atual, imagem_base64: imagem, remover_imagem: false }));
    } catch (error: any) {
      Swal.fire({ icon: 'error', title: 'Nao foi possivel carregar a imagem', text: error.message });
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSalvando(true);

    try {
      const payload = {
        nome: form.nome,
        ordem: Number(form.ordem) || 0,
        imagem_base64: form.imagem_base64,
        remover_imagem: form.remover_imagem,
      };

      if (editando) {
        await atualizarCategoria(editando.id, payload);
      } else {
        await criarCategoria(payload);
      }

      cancelarEdicao();
      carregar();
      Swal.fire({ icon: 'success', title: 'Categoria salva!', timer: 1000, showConfirmButton: false });
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Nao foi possivel salvar',
        text: error.response?.data?.erro || 'Verifique os dados informados.',
      });
    } finally {
      setSalvando(false);
    }
  }

  async function handleRemover(categoria: Categoria) {
    const confirmacao = await Swal.fire({
      icon: 'warning',
      title: `Remover "${categoria.nome}"?`,
      text: 'Os produtos dessa categoria ficarao sem categoria.',
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmacao.isConfirmed) return;

    await removerCategoria(categoria.id);
    carregar();
  }

  async function copiarLink() {
    await navigator.clipboard.writeText(linkPublico);
    Swal.fire({ icon: 'success', title: 'Link copiado!', timer: 1000, showConfirmButton: false });
  }

  return (
    <AppLayout>
      <div className="lizzi-cabecalho-pagina">
        <h1>
          <span className="lizzi-icone-titulo">
            <FiBookOpen size={18} />
          </span>
          Cardapio
        </h1>
      </div>

      <div className="lizzi-grid-2">
        <Card className="lizzi-section-card">
          <div className="lizzi-section-header">
            <div>
              <h2>{editando ? 'Editar categoria' : 'Nova categoria'}</h2>
              <p>Defina nome, ordem e imagem.</p>
            </div>
            {editando ? (
              <Button type="button" variant="outline" onClick={cancelarEdicao} fullWidth={false}>
                Cancelar
              </Button>
            ) : null}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="lizzi-form-grid-2">
              <Input label="Nome da categoria" value={form.nome} onChange={handleChange('nome')} required />
              <Input label="Ordem" type="number" value={form.ordem} onChange={handleChange('ordem')} />
              <div className="lizzi-grid-span-2">
                <ImageField
                  id="categoria-imagem"
                  label="Imagem da categoria"
                  previewUrl={form.imagem_base64 || (form.remover_imagem ? null : editando?.imagem)}
                  onChange={handleImagem}
                  onRemove={() => setForm((atual) => ({ ...atual, imagem_base64: null, remover_imagem: true }))}
                  helperText="Ideal para destacar a categoria no cardapio publico"
                  variant="wide"
                />
              </div>
            </div>

            <div className="lizzi-modal-actions" style={{ justifyContent: 'flex-start' }}>
              <Button type="submit" disabled={salvando} fullWidth={false}>
                {salvando ? 'Salvando...' : editando ? 'Salvar categoria' : 'Adicionar categoria'}
              </Button>
            </div>
          </form>
        </Card>

        <Card className="lizzi-section-card lizzi-cardapio-share-card">
          <div className="lizzi-section-header">
            <div>
              <h2>Cardapio publico</h2>
              <p>Compartilhe com QR Code e link direto.</p>
            </div>
          </div>

          {usuario?.tenant_id ? <QrCode value={linkPublico} size={180} /> : null}
          <p className="lizzi-muted-center">{linkPublico}</p>
          <Button variant="outline" onClick={copiarLink} fullWidth={false}>
            <FiCopy size={15} style={{ marginRight: '6px' }} />
            Copiar link
          </Button>
        </Card>
      </div>

      <Card className="lizzi-section-card" style={{ marginTop: '20px' }}>
        <div className="lizzi-section-header">
          <div>
            <h2>Categorias</h2>
            <p>Organize a ordem e personalize com imagem.</p>
          </div>
        </div>

        {carregando ? (
          <p>Carregando...</p>
        ) : categoriasOrdenadas.length === 0 ? (
          <div className="lizzi-empty-state">
            <FiImage size={18} />
            <span>Nenhuma categoria cadastrada ainda.</span>
          </div>
        ) : (
          <div className="lizzi-categoria-grid">
            {categoriasOrdenadas.map((categoria) => (
              <div key={categoria.id} className="lizzi-categoria-card">
                <div className="lizzi-categoria-thumb">
                  {categoria.imagem ? <img src={categoria.imagem} alt={categoria.nome} /> : <span>{categoria.nome.slice(0, 1)}</span>}
                </div>
                <div className="lizzi-categoria-copy">
                  <strong>{categoria.nome}</strong>
                  <small>Ordem {categoria.ordem}</small>
                </div>
                <div className="lizzi-categoria-actions">
                  <button type="button" onClick={() => iniciarEdicao(categoria)} aria-label="Editar categoria">
                    <FiEdit2 size={15} />
                  </button>
                  <button type="button" onClick={() => handleRemover(categoria)} aria-label="Remover categoria">
                    <FiTrash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </AppLayout>
  );
}
